/**
 * 「さらに表示する」で続きを読み足す一覧の共通部品（issue #242・#278・#284／docs/06 決定22）。
 *
 * 管理画面の一覧は黙って切りません。1回に読む件数を超える分は、サーバが返す続きの位置を
 * 次の呼び出しにそのまま渡して、下に読み足します。この部品が受け持つのは次の3つです。
 *
 *  - **条件を変えたら、読みかけの続きを捨てる。** 絞り込み・タブ・状態を変えたあとに、
 *    前の条件で頼んでいた続きが届いても、新しい一覧には足しません（別の条件の行が混ざるため）
 *  - **同じ行を二度並べない。** すでに出ている行（同じID）は足しません。トークの一覧のように、
 *    見ている間に動いた行が一覧の下にもう一度届くことがある一覧もあります（issue #293）。
 *    届いた行がすべて出ている行だったときは、押し直さなくてよいよう続けて次を読みます
 *  - **操作のあとの読み直しで、読み足した分と表示の位置を消さない**（issue #424）。
 *    `refresh` は、いま出ている件数ぶんを最初から読み直し、読み終えてから**そっくり差し替えます**。
 *    読み直している間も今の行を出したままにするので、画面の位置が先頭へ戻りません
 *  - 読み込み中・読めなかったとき・続きがあるかを、画面が言葉で出せる形で返します
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, messageForStatus } from './client.js';
import { loadFailure, type LoadFailure } from '../api/load-failure.js';

/** 1回分の答え（行と、続きの位置。続きの位置が空なら、これで全部）。 */
export interface PagedChunk<T> {
  items: T[];
  next: string | null;
}

export interface PagedList<T> {
  /** ここまでに読んだ行（最初の1回を読み終えるまでは null） */
  items: T[] | null;
  /** まだ続きがあるか */
  hasMore: boolean;
  /** 最初の1回が読めなかったときの理由（種類つき。権限が無いときは読み込み直しを出さない） */
  error: LoadFailure | null;
  /** 続きを読んでいる最中か */
  loadingMore: boolean;
  /** 続きが読めなかったときの理由 */
  moreError: string | null;
  /** 続きを読んで下に足す */
  loadMore: () => Promise<void>;
  /**
   * 最初から読み直す（読み足した分は捨て、1回目の件数に戻る）。
   * 条件を変えたとき・最初の読み込みに失敗したときに使う
   */
  reload: () => void;
  /**
   * **いま出ている件数ぶん**を最初から読み直して差し替える（操作のあと・issue #424）。
   * 読み足した分と表示の位置を消さない。読み直している間は今の行を出したまま
   */
  refresh: () => Promise<void>;
  /** 差し替えのための読み直しの最中か */
  refreshing: boolean;
  /** 差し替えのための読み直しができなかったときの理由（今の行はそのまま出している） */
  refreshError: string | null;
}

/** 読み直すときに、続けて読む回数の上限（件数の多い一覧でも止まらないように） */
const MAX_CHUNKS_PER_REFRESH = 50;
/** 届いた行がすべて出ている行だったときに、続けて読む回数の上限 */
const MAX_EMPTY_FOLLOWUPS = 10;

/**
 * @param queryKey 一覧の条件を表す文字列。**変わったら最初から読み直し、読みかけの続きは捨てる**
 * @param fetchChunk 続きの位置（最初は null）を受け取って1回分を読む
 * @param idOf 行の見分け方（同じ行を二度並べないため）
 */
export function usePagedList<T>(
  queryKey: string,
  fetchChunk: (cursor: string | null) => Promise<PagedChunk<T>>,
  idOf: (item: T) => string,
): PagedList<T> {
  const [items, setItems] = useState<T[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<LoadFailure | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // 読み直すたびに1つ進める番号。届いた答えの番号が今と違えば、古い条件の答えとして捨てる
  const generation = useRef(0);
  const inFlight = useRef(false);
  // 呼び出し元が毎回新しい関数を渡しても読み直さないよう、最新の関数だけを覚えておく
  const fetchRef = useRef(fetchChunk);
  const idRef = useRef(idOf);
  useEffect(() => {
    fetchRef.current = fetchChunk;
    idRef.current = idOf;
  });

  useEffect(() => {
    generation.current += 1;
    const mine = generation.current;
    inFlight.current = false;
    setItems(null);
    setCursor(null);
    setError(null);
    setMoreError(null);
    setLoadingMore(false);
    setRefreshing(false);
    setRefreshError(null);
    fetchRef
      .current(null)
      .then((chunk) => {
        if (mine !== generation.current) return;
        setItems(chunk.items);
        setCursor(chunk.next);
      })
      .catch((caught: unknown) => {
        if (mine !== generation.current) return;
        setError(loadFailure(caught));
      });
    // 画面を離れた・条件が変わったあとに届いた答えは、どれも捨てる
    return () => {
      generation.current += 1;
    };
  }, [queryKey, reloadKey]);

  const loadMore = useCallback(async (): Promise<void> => {
    if (cursor === null || items === null || inFlight.current) return;
    const mine = generation.current;
    inFlight.current = true;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const seen = new Set(items.map((item) => idRef.current(item)));
      const added: T[] = [];
      let next: string | null = cursor;
      // 届いた行がすべて出ている行だったとき（見ている間に動いたトークなど）は、
      // 「押したのに何も増えない」にしないよう、続けて次を読む
      for (let round = 0; round <= MAX_EMPTY_FOLLOWUPS && next !== null; round += 1) {
        const chunk: PagedChunk<T> = await fetchRef.current(next);
        // 待っている間に条件が変わった（または読み直した）ときは、新しい一覧に足さない
        if (mine !== generation.current) return;
        for (const item of chunk.items) {
          const id = idRef.current(item);
          if (seen.has(id)) continue;
          seen.add(id);
          added.push(item);
        }
        next = chunk.next;
        if (added.length > 0) break;
      }
      setItems((prev) => [...(prev ?? []), ...added]);
      setCursor(next);
    } catch (caught: unknown) {
      if (mine !== generation.current) return;
      setMoreError(toMessage(caught));
    } finally {
      if (mine === generation.current) {
        inFlight.current = false;
        setLoadingMore(false);
      }
    }
  }, [cursor, items]);

  const reload = useCallback(() => setReloadKey((n) => n + 1), []);

  const refresh = useCallback(async (): Promise<void> => {
    // まだ1回目を読んでいない（読めなかった）ときは、ふつうに最初から読む
    if (items === null) {
      reload();
      return;
    }
    const want = items.length;
    // 読み足しの途中なら、その答えは捨てる（読み直した一覧に古い続きを足さない）
    generation.current += 1;
    const mine = generation.current;
    inFlight.current = true;
    setLoadingMore(false);
    setMoreError(null);
    setRefreshing(true);
    setRefreshError(null);
    try {
      const collected: T[] = [];
      const seen = new Set<string>();
      let next: string | null = null;
      for (let round = 0; round < MAX_CHUNKS_PER_REFRESH; round += 1) {
        const chunk: PagedChunk<T> = await fetchRef.current(next);
        if (mine !== generation.current) return;
        for (const item of chunk.items) {
          const id = idRef.current(item);
          if (seen.has(id)) continue;
          seen.add(id);
          collected.push(item);
        }
        next = chunk.next;
        if (next === null || collected.length >= want) break;
      }
      setItems(collected);
      setCursor(next);
    } catch (caught: unknown) {
      if (mine !== generation.current) return;
      setRefreshError(toMessage(caught));
    } finally {
      if (mine === generation.current) {
        inFlight.current = false;
        setRefreshing(false);
      }
    }
  }, [items, reload]);

  return {
    items,
    hasMore: items !== null && cursor !== null,
    error,
    loadingMore,
    moreError,
    loadMore,
    reload,
    refresh,
    refreshing,
    refreshError,
  };
}

function toMessage(caught: unknown): string {
  return caught instanceof ApiError ? caught.message : messageForStatus(0);
}
